import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Navbar } from './components/layout/Navbar';
import { useAuth } from './context/AuthContext';

// Lazy load pages for code splitting
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const Browse = lazy(() => import('./pages/Browse').then(m => ({ default: m.Browse })));
const Watch = lazy(() => import('./pages/Watch').then(m => ({ default: m.Watch })));
const Search = lazy(() => import('./pages/Search').then(m => ({ default: m.Search })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Favorites = lazy(() => import('./pages/Favorites').then(m => ({ default: m.Favorites })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminUpload = lazy(() => import('./pages/AdminUpload').then(m => ({ default: m.AdminUpload })));

// Layout components
function MainLayout() {
  return (
    <div className="min-h-screen bg-dark-50 dark:bg-dark-950">
      <Navbar />
      <main className="container-main py-6">
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </main>
      <footer className="border-t border-dark-200 dark:border-dark-800 py-8 mt-16">
        <div className="container-main text-center text-dark-500 text-sm">
          <p>StreamPlatform - Premium streaming experience</p>
        </div>
      </footer>
      <Toaster />
    </div>
  );
}

function AuthLayout() {
  return (
    <div className="min-h-screen bg-dark-50 dark:bg-dark-950 flex items-center justify-center p-4">
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
      <Toaster />
    </div>
  );
}

function AdminLayout() {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) return <PageSkeleton />;
  if (!user || !isAdmin) return <Navigate to="/" replace />;
  
  return (
    <div className="min-h-screen bg-dark-50 dark:bg-dark-950">
      <Navbar />
      <div className="container-main py-6">
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </div>
      <Toaster />
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="h-8 w-48 bg-dark-200 dark:bg-dark-700 rounded" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <div className="aspect-video bg-dark-200 dark:bg-dark-700 rounded-xl" />
            <div className="h-4 w-3/4 bg-dark-200 dark:bg-dark-700 rounded" />
            <div className="h-4 w-1/2 bg-dark-200 dark:bg-dark-700 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<MainLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/search" element={<Search />} />
        <Route path="/watch/:slug" element={<Watch />} />
      </Route>
      
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
      </Route>
      
      <Route element={<MainLayout />}>
        <Route path="/profile" element={<Profile />} />
        <Route path="/history" element={<History />} />
        <Route path="/favorites" element={<Favorites />} />
      </Route>
      
      <Route element={<AdminLayout />}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/upload" element={<AdminUpload />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}